import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import MessagesContainer from './MessagesContainer';
import SendMessage from './SendMessage';
import ChannelHeader from './ChannelHeader';
import FileUpload from '../FileUpload';
import { Mutation, Query } from 'react-apollo';
import moment from 'moment';
import actions from '../../ducks/actions';
import AttachmentDetail from './AttachmentDetail';
import { GET_CHANNEL_MESSAGES, GET_CHANNEL_MEMBERS } from '../../graphql/queries';
import { POST_MESSAGE_TO_CHANNEL } from '../../graphql/mutations';
import AttachmentUpload from './AttachmentUpload';
import './ChannelMessages.css';

class ChannelMessages extends Component {
  constructor (props) {
    super(props);

    this.channelHasMoreMessages = true;
    this.scrollerRef = React.createRef();

    this.state = {
      initialFetchTimestamp: moment().toISOString(),
      showAttachmentDetailModal: false,
      attachmentDetailAttachment: null,
      showAttachmentUploadModal: false,
      attachmentUploadFile: null
    };
  }

  componentDidUpdate = (prevProps, prevState) => {
    // previously uploading a file and now it has completed, dismiss the modal
    if (
      prevProps.uploadingAttachment && !this.props.uploadingAttachment &&
      prevState.showAttachmentUploadModal && this.state.showAttachmentUploadModal
    ) {
      this.toggleAttachmentUploadModal(null);
    }

    // if the channel changed then fetch any new messages
    if (prevProps.activeChannelId !== this.props.activeChannelId) {
      this.setState({ initialFetchTimestamp: moment().toISOString() });
    }
  }

  /**
   * function that makes use of react-apollo's fetchMore function
   * Note: wanted to hoist this out of render()
   *
   * @param {String} timestamp
   * @param {Function} fetchMoreGql
   */
  fetchMoreMessages = (timestamp, fetchMoreGql) => {
    fetchMoreGql({
      variables: {
        channelId: this.props.activeChannelId,
        datetime: timestamp
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const { getChannelMessagesSince: previous } = previousResult;
        const { getChannelMessagesSince: fetched } = fetchMoreResult;

        if (!fetchMoreResult || fetched.length === 0) {
          this.channelHasMoreMessages = false;

          return previousResult;
        }

        return {
          ...previousResult,
          getChannelMessagesSince: [...previous, ...fetched]
        };
      }
    });
  }

  /**
   * Dispatches action to upload a file to s3
   * @param {File} file
   */
  uploadAttachmentToChannel = file => {
    if (file) {
      // this.props.uploadAttachment(file, this.props.activeChannelId);
      this.toggleAttachmentUploadModal(file);
    }
  }

  handleFileSelect = event => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.target.files[0];

    // this.uploadAttachmentToChannel(file);
    this.toggleAttachmentUploadModal(file);
  }

  toggleAttachmentUploadModal = file => {
    this.setState({
      showAttachmentUploadModal: Boolean(file),
      attachmentUploadFile: file
    });
  }

  handleAttachmentClick = attachment => this.setState({ showAttachmentDetailModal: true, attachmentDetailAttachment: attachment });

  render () {
    const { activeChannelId, activeChannelDetails } = this.props;

    if (!activeChannelId) return null;

    return (
      <React.Fragment>
        <Query
          query={GET_CHANNEL_MEMBERS}
          variables={{ channelId: this.props.activeChannelId }}
          skip={this.state.activeChannelId === null}
          fetchPolicy='cache-and-network'
        >
          {({ data }) => (
            <ChannelHeader
              name={activeChannelDetails ? activeChannelDetails.name : ''}
              topic={activeChannelDetails ? activeChannelDetails.topic : ''}
              toggleChannelDetailPanel={this.props.toggleChannelDetailPanelViaHeaderAction}
              channelMemberCount={data && data.getChannelMembers ? data.getChannelMembers.length : 0}
              toggleChannelInformationModal={this.props.toggleChannelInformationModal}
              isDirectMessage={this.props.activeChannelDetails.directMessage}
              toggleChannelMembersDialog={this.props.toggleChannelMembersDialog}
            />
          )}
        </Query>
        <Query
          notifyOnNetworkStatusChange
          query={GET_CHANNEL_MESSAGES}
          variables={{
            channelId: this.props.activeChannelId,
            datetime: this.props.channelMessagesInitialFetchTimestamps[this.props.activeChannelId]
          }}
          fetchPolicy='network-only'
        >
          {({ loading, error, data, fetchMore }) => (
            <FileUpload onFileDrop={this.uploadAttachmentToChannel}>
              <MessagesContainer
                scrollerRef={this.scrollerRef}
                loadingMessages={loading}
                messages={data && data.getChannelMessagesSince ? data.getChannelMessagesSince : []}
                fetchMoreMessages={timestamp => this.fetchMoreMessages(timestamp, fetchMore)}
                hasMoreMessages={this.channelHasMoreMessages}
                onAttachmentClick={this.handleAttachmentClick}
                activeChannelId={this.props.activeChannelId}
                currentUserId={this.props.currentUserId}
              />
            </FileUpload>
          )}
        </Query>
        <Mutation mutation={POST_MESSAGE_TO_CHANNEL}>
          {(postMessageToChannel, { error, loading }) => {
            if (error && error.graphQLErrors.length) {
              if (error.graphQLErrors[0].message.match(/archived/i)) {
                alert('This channel has been archived. Messages can no longer be sent in this channel.');
              }
            }

            return (
              <SendMessage
                placeholder={activeChannelDetails ? activeChannelDetails.name : ''}
                onSubmit={message => {
                  this.scrollerRef.current.scrollIntoView({ behaivor: 'smooth' });

                  postMessageToChannel({
                    variables: {
                      channelId: activeChannelId,
                      message
                    }
                  });
                }}
                onFileSelect={this.handleFileSelect}
                loading={loading}
              />
            );
          }}
        </Mutation>
        <AttachmentDetail
          open={this.state.showAttachmentDetailModal}
          attachment={this.state.attachmentDetailAttachment}
          onClose={() => this.setState({ showAttachmentDetailModal: false, attachmentDetailAttachment: null })}
        />
        <AttachmentUpload
          isOpen={this.state.showAttachmentUploadModal}
          attachment={this.state.attachmentUploadFile}
          channelName={this.props.activeChannelDetails.directMessage ? this.props.activeChannelDetails.name : `#${this.props.activeChannelDetails.name}`}
          onClose={() => this.toggleAttachmentUploadModal(null)}
          handleUpload={(file, message) => this.props.uploadAttachment(file, message, this.props.activeChannelId)}
          uploading={this.props.uploadingAttachment}
        />
      </React.Fragment>
    );
  }
}

ChannelMessages.propTypes = {
  activeChannelId: PropTypes.string,
  activeChannelDetails: PropTypes.object,
  channelReceivedNewMessage: PropTypes.func,
  toggleChannelInformationModal: PropTypes.func,
  toggleChannelMembersDialog: PropTypes.func
};

function mapStateToProps (state) {
  return {
    uploadingAttachment: state.messages.meta.uploadingAttachment,
    currentUserId: `employee-${state.identity.legacyUserId}`,
    channelMessagesInitialFetchTimestamps: state.messages.channelMessagesInitialFetchTimestamps
  };
}

function mapDispatchToProps (dispatch) {
  return {
    uploadAttachment: (file, message, channelId) => dispatch(actions.uploadAttachmentToChannelRequest(file, message, channelId))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ChannelMessages);
